import React, { useEffect, useMemo, useState } from "react";
import { ActivityCard, PlayButton, ProgressPill } from "../shared";

/**
 * NounSwapIntro — "Why Pronouns?" hook.
 *
 * Shows short paragraphs that clumsily repeat a name; tapping a highlighted
 * name swaps it for the correct pronoun with the English gloss updating live.
 * Completes when every swap has been made.
 */
export default function NounSwapIntro({ data, onComplete, audio }) {
  const [swappedIds, setSwappedIds] = useState(() => new Set());

  const swappable = useMemo(
    () => data.paragraphs.flatMap((p) => p.sentences.filter((s) => s.swap)),
    [data]
  );
  const allSwapped = swappedIds.size >= swappable.length;

  useEffect(() => {
    if (allSwapped) onComplete();
  }, [allSwapped, onComplete]);

  const handleSwap = (sentence) => {
    if (swappedIds.has(sentence.id)) return;
    setSwappedIds((prev) => new Set(prev).add(sentence.id));
    if (audio) audio.play(sentence.swap.ar_after);
  };

  return (
    <ActivityCard>
      <div className="flex items-center justify-end mb-4">
        <ProgressPill>{swappedIds.size}/{swappable.length} fixed</ProgressPill>
      </div>

      <div className="flex flex-col gap-4">
        {data.paragraphs.map((paragraph) => (
          <div
            key={paragraph.id}
            className="rounded-xl px-4 py-4"
            style={{ background: "var(--ink)", border: "1px solid var(--panel-line)" }}
          >
            <p dir="rtl" lang="ar" className="font-arabic text-2xl sm:text-[1.7rem] leading-[2.2] m-0 text-right">
              {paragraph.sentences.map((sentence) => {
                const swapped = sentence.swap && swappedIds.has(sentence.id);
                if (!sentence.swap) {
                  return (
                    <span key={sentence.id} style={{ color: "var(--parchment)" }}>{sentence.ar}{" "}</span>
                  );
                }
                if (swapped) {
                  const [pronoun, ...rest] = sentence.swap.ar_after.split(" ");
                  return (
                    <span key={sentence.id}>
                      <span className="font-semibold" style={{ color: "var(--tag-violet)" }}>{pronoun}</span>{" "}
                      <span style={{ color: "var(--parchment)" }}>{rest.join(" ")}</span>{" "}
                    </span>
                  );
                }
                const rest = sentence.ar.startsWith(sentence.swap.noun_ar)
                  ? sentence.ar.slice(sentence.swap.noun_ar.length)
                  : sentence.ar;
                return (
                  <span key={sentence.id}>
                    <button
                      type="button"
                      onClick={() => handleSwap(sentence)}
                      className="font-arabic rounded-md px-1 transition-all hover:brightness-125 animate-pulse"
                      style={{
                        color: "var(--gold-bright)",
                        background: "var(--gold-bg)",
                        border: "1px dashed var(--gold-dim)",
                        fontSize: "inherit",
                        lineHeight: "inherit",
                      }}
                      title="Tap to replace with a pronoun"
                    >
                      {sentence.swap.noun_ar}
                    </button>
                    <span style={{ color: "var(--parchment)" }}>{rest}{" "}</span>
                  </span>
                );
              })}
            </p>
            <p className="text-sm mt-2 mb-0" style={{ color: "var(--parchment-dim)" }}>
              {paragraph.sentences
                .map((s) => (s.swap && swappedIds.has(s.id) ? s.swap.en_after : s.en))
                .join(" ")}
              {audio && (
                <PlayButton
                  className="ml-2 align-middle scale-90"
                  text={paragraph.sentences
                    .map((s) => (s.swap && swappedIds.has(s.id) ? s.swap.ar_after : s.ar))
                    .join(" ")}
                  playingText={audio.playingText}
                  onPlay={audio.play}
                />
              )}
            </p>
          </div>
        ))}
      </div>

      {allSwapped && (
        <div
          className="flex items-start gap-2.5 mt-4 px-4 py-3 rounded-xl"
          style={{ background: "var(--violet-bg)", border: "1px solid var(--panel-line)" }}
        >
          <span className="text-lg leading-none" aria-hidden="true">✨</span>
          <p className="text-sm leading-relaxed m-0" style={{ color: "var(--parchment)" }}>{data.takeaway}</p>
        </div>
      )}
    </ActivityCard>
  );
}
